import { useCallback } from "react";
import { toast } from "sonner";

import { sanitizeDownloadFilename } from "../../../helpers/downloadFilename";
import {
  FilesystemAPI,
  type ZipJob,
  type ZipRequest,
  type ZipTree
} from "../../caseFiles/api/filesystemApi";
import { formatBytes } from "../../caseFiles/fileKind";
import { useCaseIde } from "../context/CaseIdeContext";

export type ZipDownload = {
  tree: ZipTree;
  request: ZipRequest;
  /** What the toast calls the archive while it is prepared ("Case Files.zip"). */
  name: string;
  /** How many files the archive should hold, for "n of N". */
  total: number;
};

const POLL_MS = 1500;

const fileCount = (n: number) => `${n} file${n === 1 ? "" : "s"}`;

const clickLink = (href: string, download?: string) => {
  const anchor = document.createElement("a");
  anchor.href = href;
  if (download) anchor.download = sanitizeDownloadFilename(download);
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

export const saveBlob = (blob: Blob, name: string) => {
  const url = URL.createObjectURL(blob);
  clickLink(url, name);
  URL.revokeObjectURL(url);
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Build a zip and hand it to the browser, narrating it in one sonner toast:
 * "Preparing · n of N" with Cancel, then done. A small archive streams back
 * and saves at once; a large one is a server job, polled until its signed
 * link is ready, and the toast's Download opens it.
 */
export const runZipDownload = async (
  individualId: string,
  { tree, request, name, total }: ZipDownload,
  pollMs: number = POLL_MS
): Promise<void> => {
  const id = `zip:${name}:${Date.now()}`;
  const controller = new AbortController();
  let jobId: string | null = null;
  let canceled = false;

  const cancel = () => {
    canceled = true;
    controller.abort();
    if (jobId) {
      FilesystemAPI.cancelZip(individualId, jobId).catch(() => undefined);
    }
    toast.dismiss(id);
    toast.message(`Canceled ${name}`);
  };
  const preparing = (description: string) =>
    toast.loading(`Preparing ${name}`, {
      id,
      description,
      duration: Infinity,
      cancel: { label: "Cancel", onClick: cancel }
    });

  preparing(`0 of ${fileCount(total)}`);
  try {
    const result = await FilesystemAPI.zip(individualId, tree, request, {
      signal: controller.signal,
      onProgress: (loaded) => {
        if (!canceled && loaded > 0)
          preparing(`Downloading · ${formatBytes(loaded)}`);
      }
    });
    if (canceled) return;
    if (result.kind === "file") {
      saveBlob(result.blob, result.filename);
      toast.success(`Downloaded ${result.filename}`, {
        id,
        description: fileCount(total),
        duration: 4000,
        cancel: undefined
      });
      return;
    }

    let { job } = result;
    jobId = job.job_id;
    while (job.status === "processing") {
      preparing(`${Math.min(job.done, job.total)} of ${fileCount(job.total)}`);
      // eslint-disable-next-line no-await-in-loop
      await sleep(pollMs);
      if (canceled) return;
      // eslint-disable-next-line no-await-in-loop
      job = (await FilesystemAPI.zipStatus(individualId, job.job_id)).job;
      if (canceled) return;
    }
    if (job.status === "ready" && job.download_url) {
      const url = job.download_url;
      toast.success(`${job.download_name} is ready`, {
        id,
        description: `${fileCount(job.total)} · link valid for an hour`,
        duration: 60_000,
        cancel: undefined,
        action: { label: "Download", onClick: () => clickLink(url) }
      });
      return;
    }
    if (job.status === "canceled") {
      toast.dismiss(id);
      return;
    }
    throw new Error(job.error || "The zip could not be built.");
  } catch (error) {
    if (canceled) return;
    toast.error(`Couldn't prepare ${name}`, {
      id,
      description: error instanceof Error ? error.message : undefined,
      duration: 8000,
      cancel: undefined
    });
  }
};

/** Start a zip of this case's Case Files or Work Products. */
export const useZipDownload = () => {
  const { individualId } = useCaseIde();
  return useCallback(
    (download: ZipDownload) => runZipDownload(individualId, download),
    [individualId]
  );
};
